import {
  GAIL_UNIVERSITY_PRODUCT_TAGS,
  GailUniversityLocalizedText,
  GailUniversityProductTag,
  GailUniversityVideo,
  GailUniversityVideoGroup,
} from "@/types/gailUniversity";

const DEFAULT_GROUP_KEY = "all";
const PRODUCT_TAG_SET = new Set<string>(GAIL_UNIVERSITY_PRODUCT_TAGS);

export const GAIL_UNIVERSITY_PATH = "/gail-university";

export type GailUniversityProductFilter = "all" | GailUniversityProductTag;

/** Missing, `all`, and unknown values all mean the unfiltered catalog. */
export function parseGailUniversityProductFilter(
  value: string | null | undefined
): GailUniversityProductFilter {
  if (value && PRODUCT_TAG_SET.has(value)) {
    return value as GailUniversityProductTag;
  }
  return "all";
}

export function gailUniversityHref(filter: GailUniversityProductFilter): string {
  if (filter === "all") return GAIL_UNIVERSITY_PATH;
  return `${GAIL_UNIVERSITY_PATH}?productTag=${filter}`;
}

export function getLocalizedText(
  value: GailUniversityLocalizedText | null | undefined,
  language: string,
  fallback = ""
): string {
  if (!value) return fallback;

  const normalized = language.toLowerCase();
  const shortLanguage = normalized.split("-")[0];

  return (
    value[normalized] ??
    value[shortLanguage] ??
    value.en ??
    Object.values(value).find(Boolean) ??
    fallback
  );
}

export function groupGailUniversityVideos(
  videos: GailUniversityVideo[],
  language: string,
  fallbackGroupName: string
): GailUniversityVideoGroup[] {
  const groups = new Map<string, GailUniversityVideoGroup>();

  for (const video of videos) {
    const key = video.groupKey || DEFAULT_GROUP_KEY;
    const existing = groups.get(key);
    const groupName = getLocalizedText(video.groupName, language, fallbackGroupName);
    const hasExplicitGroupName = Boolean(getLocalizedText(video.groupName, language));

    if (existing) {
      existing.videos.push(video);
      existing.order = Math.min(existing.order, video.groupOrder ?? 0);
      if (hasExplicitGroupName && existing.name === fallbackGroupName) {
        existing.name = groupName;
      }
      continue;
    }

    groups.set(key, {
      key,
      name: groupName,
      order: video.groupOrder ?? 0,
      videos: [video],
    });
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      videos: [...group.videos].sort((a, b) => (
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )),
    }))
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}
